import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './BattleRepCount.css';

const BattleRepCount = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { battleCode: urlBattleCode } = useParams();
    const [repCount, setRepCount] = useState(10);
    const setCount = 1; // Fixed to 1 set

    // Get battle code from multiple sources (same pattern as other components)
    const getBattleCode = () => {
        // First try URL params
        if (urlBattleCode && urlBattleCode !== 'undefined' && urlBattleCode !== 'unknown') {
            console.log('BattleRepCount - Battle code found in URL params:', urlBattleCode);
            return decodeURIComponent(urlBattleCode);
        }

        // Then try location state
        if (location.state?.battleCode) {
            console.log('BattleRepCount - Battle code found in location state:', location.state.battleCode);
            return location.state.battleCode;
        }

        // Then try sessionStorage navigation data
        try {
            const battleNavData = sessionStorage.getItem('battleNavData');
            if (battleNavData) {
                const data = JSON.parse(battleNavData);
                if (data.battleCode && data.battleCode !== 'undefined') {
                    console.log('BattleRepCount - Battle code found in nav data:', data.battleCode);
                    return data.battleCode;
                }
            }
        } catch (e) {
            console.log('Error parsing battleNavData from session storage');
        }

        // Then try direct sessionStorage
        const sessionBattleCode = sessionStorage.getItem('currentBattleCode');
        if (sessionBattleCode && sessionBattleCode !== 'undefined') {
            console.log('BattleRepCount - Battle code found in session storage:', sessionBattleCode);
            return sessionBattleCode;
        }

        // Finally try localStorage
        const localBattleCode = localStorage.getItem('lastBattleCode');
        if (localBattleCode && localBattleCode !== 'undefined') {
            console.log('BattleRepCount - Battle code found in local storage:', localBattleCode);
            return localBattleCode;
        }

        console.warn('BattleRepCount - Battle code not found in any source');
        return null;
    };

    const actualBattleCode = getBattleCode();

    // Debug logging on mount
    useEffect(() => {
        console.log('BattleRepCount Debug Info:');
        console.log('URL battle code:', urlBattleCode);
        console.log('Location state:', location.state);
        console.log('Session storage currentBattleCode:', sessionStorage.getItem('currentBattleCode'));
        console.log('Session storage battleNavData:', sessionStorage.getItem('battleNavData'));
        console.log('Local storage lastBattleCode:', localStorage.getItem('lastBattleCode'));
        console.log('Final battle code:', actualBattleCode);

        // If we have a battle code, save it to all storage locations for downstream components
        if (actualBattleCode) {
            sessionStorage.setItem('currentBattleCode', actualBattleCode);
            sessionStorage.setItem('selectedBattleCode', actualBattleCode);
            localStorage.setItem('lastBattleCode', actualBattleCode);
            localStorage.setItem('currentBattleSelection', actualBattleCode);
        } else {
            // If no battle code found, redirect back to selection
            console.error('No battle code available, redirecting to battle selection');
            navigate('/battle-selection');
            return;
        }
    }, [actualBattleCode, navigate]);

    // Calculate total battle intensity (simplified since setCount is always 1)
    const totalReps = repCount;
    const battleIntensity = totalReps <= 10 ? 'Rookie' :
        totalReps <= 20 ? 'Warrior' :
            totalReps <= 30 ? 'Champion' :
                totalReps <= 40 ? 'Legend' : 'XEROMEN';

    // Get battle intensity color
    const getIntensityColor = () => {
        switch (battleIntensity) {
            case 'Rookie': return '#4CAF50';
            case 'Warrior': return '#FF9800';
            case 'Champion': return '#2196F3';
            case 'Legend': return '#9C27B0';
            case 'XEROMEN': return '#F44336';
            default: return '#FF0000';
        }
    };

    // Handle starting the battle with enhanced battle code preservation
    const handleStartBattle = () => {
        if (repCount > 0 && actualBattleCode) {
            console.log('BattleRepCount - Starting battle with:');
            console.log('Battle Code:', actualBattleCode);
            console.log('Rep Count:', repCount);
            console.log('Set Count:', setCount);

            // QUADRUPLE STORAGE: Save battle parameters in multiple places
            // 1. Session storage with multiple keys
            sessionStorage.setItem('currentBattleCode', actualBattleCode);
            sessionStorage.setItem('selectedBattleCode', actualBattleCode);
            sessionStorage.setItem('battleRepCount', repCount.toString());
            sessionStorage.setItem('battleSetCount', setCount.toString());

            // 2. Local storage for persistence
            localStorage.setItem('lastBattleCode', actualBattleCode);
            localStorage.setItem('lastBattleRepCount', repCount.toString());
            localStorage.setItem('lastBattleSetCount', setCount.toString());

            // 3. Prepare comprehensive workout data
            const battleWorkoutData = {
                battleCode: actualBattleCode,
                repCount: repCount,
                setCount: setCount,
                totalReps: totalReps,
                battleIntensity: battleIntensity,
                timestamp: new Date().toISOString(),
                source: 'BattleRepCount'
            };

            // 4. Save complete workout data
            sessionStorage.setItem('battleWorkoutData', JSON.stringify(battleWorkoutData));

            // Navigate with properly encoded battle code
            const encodedBattleCode = encodeURIComponent(actualBattleCode);

            console.log('Navigating to battle-workout with encoded code:', encodedBattleCode);
            console.log('Battle workout data:', battleWorkoutData);

            navigate(`/battle-workout/${encodedBattleCode}/${repCount}/${setCount}`, {
                state: battleWorkoutData
            });
        } else {
            if (!actualBattleCode) {
                alert('Battle code is missing! Returning to battle selection.');
                navigate('/battle-selection');
            } else {
                alert('Please set valid rep count!');
            }
        }
    };

    // Generate random background
    const getRandomBackground = () => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    };

    // Show loading if no battle code
    if (!actualBattleCode) {
        return (
            <div className="battle-rep-count-container">
                <div className="battle-rep-overlay"></div>
                <div className="battle-loading">
                    <h2>⚔️ Loading Battle... ⚔️</h2>
                    <p>Retrieving battle code...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="battle-rep-count-container"
            style={{
                backgroundImage: getRandomBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Battle overlay */}
            <div className="battle-rep-overlay"></div>

            <div className="battle-rep-content">
                {/* Header */}
                <div className="battle-rep-header">
                    <button
                        className="battle-rep-back-button"
                        onClick={() => navigate('/battle-selection')}
                    >
                        ← Back to Selection
                    </button>

                    <div className="battle-code-display">
                        <h1>⚔️ BATTLE CODE ⚔️</h1>
                        <div className="battle-code">{actualBattleCode}</div>
                    </div>
                </div>

                {/* Battle preparation card */}
                <div className="battle-prep-card">
                    <h2 className="battle-prep-title">🛡️ PREPARE FOR BATTLE 🛡️</h2>

                    {/* Rep selection - large centered input with +/- buttons */}
                    <div className="battle-setting-section">
                        <h3>⚔️ Reps per Exercise</h3>
                        <div className="rep-input-section-centered">
                            <div className="rep-counter-battle" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '20px'
                            }}>
                                <button
                                    className="counter-button-battle"
                                    onClick={() => setRepCount(prev => prev > 1 ? prev - 1 : 1)}
                                    disabled={repCount <= 1}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        fontSize: '2rem',
                                        backgroundColor: repCount <= 1 ? '#666' : '#FFD700',
                                        color: '#000000',
                                        border: 'none',
                                        borderRadius: '50%',
                                        cursor: repCount <= 1 ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold',
                                        transition: 'all 0.2s',
                                        boxShadow: repCount <= 1 ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.3), 0 0 10px rgba(255, 215, 0, 0.3)'
                                    }}
                                >
                                    -
                                </button>

                                <input
                                    type="number"
                                    className="battle-rep-input-large"
                                    value={repCount}
                                    onChange={(e) => setRepCount(Math.max(1, parseInt(e.target.value) || 1))}
                                    min="1"
                                    style={{
                                        fontSize: '2.5rem',
                                        padding: '20px',
                                        width: '150px',
                                        textAlign: 'center',
                                        borderRadius: '15px',
                                        border: '3px solid #FFD700',
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        color: '#FFD700',
                                        fontWeight: 'bold'
                                    }}
                                />

                                <button
                                    className="counter-button-battle"
                                    onClick={() => setRepCount(prev => prev + 1)}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        fontSize: '2rem',
                                        backgroundColor: '#FFD700',
                                        color: '#000000',
                                        border: 'none',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), 0 0 10px rgba(255, 215, 0, 0.3)'
                                    }}
                                >
                                    +
                                </button>
                            </div>
                            <span className="rep-input-label-centered" style={{
                                display: 'block',
                                textAlign: 'center',
                                marginTop: '15px',
                                fontSize: '1.2rem',
                                color: '#FFD700',
                                fontWeight: 'bold'
                            }}>reps per exercise</span>
                        </div>
                    </div>

                    {/* Battle intensity display */}
                    <div className="battle-intensity-section">
                        <h3>💀 Battle Intensity</h3>
                        <div
                            className="battle-intensity-display"
                            style={{ borderColor: getIntensityColor() }}
                        >
                            <div
                                className="intensity-level"
                                style={{ color: getIntensityColor() }}
                            >
                                {battleIntensity}
                            </div>
                            <div className="intensity-details">
                                <span>Reps per Exercise: {repCount}</span>
                                <span>Single Set Battle</span>
                            </div>
                        </div>
                    </div>

                    {/* Battle warnings */}
                    <div className="battle-warnings">
                        <div className="warning-item">
                            <span className="warning-icon">⚠️</span>
                            <span>Villains will try to steal your reps!</span>
                        </div>
                        <div className="warning-item">
                            <span className="warning-icon">💪</span>
                            <span>Your past workouts provide Hidden Health Bonus</span>
                        </div>
                        <div className="warning-item">
                            <span className="warning-icon">🎯</span>
                            <span>Choose your moves and actions wisely</span>
                        </div>
                    </div>

                    {/* Start battle button */}
                    <button
                        className="start-battle-button"
                        onClick={handleStartBattle}
                        disabled={repCount <= 0 || !actualBattleCode}
                    >
                        <span className="button-text">⚔️ START BATTLE ⚔️</span>
                        <span className="button-subtext">
                            {!actualBattleCode ? 'Missing battle code!' : 'Face the villains!'}
                        </span>
                    </button>
                </div>

                {/* Battle tips */}
                <div className="battle-tips">
                    <h4>🧠 Battle Strategy Tips</h4>
                    <ul>
                        <li>🔴 <strong>Attack:</strong> High risk, high reward - can gain or lose big</li>
                        <li>🔵 <strong>Defend:</strong> Safer option, usually provides protection</li>
                        <li>🟣 <strong>Run:</strong> Unpredictable results, anything can happen</li>
                        <li>💚 <strong>Hidden Health:</strong> Your workout history strengthens you</li>
                        <li>⚡ <strong>Combos:</strong> Some moves work better with certain actions</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default BattleRepCount;